import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Info, Loader2, TrendingDown, TrendingUp, Upload } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MoneyAmount } from '@/components/design-system';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import { useCaisses, useComptesBancaires } from '@/hooks/queries/useBanques';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { useFormations } from '@/hooks/queries/useFormation';
import { useUpsertOperation } from '@/hooks/queries/useOperations';
import { cn } from '@/lib/utils';
import { getOperationTypes, mapTypeCodeToCategorie } from '@/lib/constants/operation-types';
import type { OperationCanal, OperationCategorie, OperationSens } from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string | null;
  onSaved?: () => void;
}

const SUB_ENTREE = 'Argent reçu (encaissement client, acompte, solde, avance)';
const SUB_SORTIE = 'Argent décaissé (paiement fournisseur, salaire, charge)';

function genReference(): string {
  const yr = new Date().getFullYear();
  const rnd = Math.floor(Math.random() * 9000 + 1000);
  return `CAS-${yr}-${rnd}`;
}

export function OperationCaisseFormDialog({
  open,
  onOpenChange,
  entrepriseId,
  onSaved,
}: Props): JSX.Element {
  const upsert = useUpsertOperation();
  const { data: entreprises = [] } = useEntreprises();
  const { data: activites = [] } = useAllActivites();
  const eurl = entreprises.find((e) => e.id === entrepriseId) ?? entreprises[0];
  // Sprint 6.8.4 — single source of truth via prop, hook gated par enabled:!!entrepriseId
  const { data: tiers = [] } = useTiers({
    entrepriseId: entrepriseId ?? eurl?.id ?? null,
    search: '',
  });
  const { data: users = [] } = useUsersList();
  const { data: formations = [] } = useFormations({});
  // Sprint 10.1 — Caisses + comptes pour fixer la contrainte operations_canal_target.
  const effectiveEntrepriseId = entrepriseId ?? eurl?.id ?? null;
  const { data: caisses = [] } = useCaisses(effectiveEntrepriseId);
  const { data: comptes = [] } = useComptesBancaires(effectiveEntrepriseId);

  const activitesScoped = useMemo(
    () => activites.filter((a) => a.is_active !== false),
    [activites],
  );

  const [sens, setSens] = useState<OperationSens | null>(null);
  const [canal, setCanal] = useState<OperationCanal | null>(null);
  // Sprint 10.1 — id de la caisse OU du compte bancaire selon le canal (fix contrainte DB).
  const [caisseOrCompteId, setCaisseOrCompteId] = useState('');
  const [activiteId, setActiviteId] = useState('');
  const [dateOp, setDateOp] = useState(new Date().toISOString().slice(0, 10));
  const [heureOp, setHeureOp] = useState(new Date().toTimeString().slice(0, 5));
  const [typeCode, setTypeCode] = useState('');
  const [categorie, setCategorie] = useState<OperationCategorie>('autre');
  const [motif, setMotif] = useState('');
  const [tierSearch, setTierSearch] = useState('');
  const [tierId, setTierId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [montant, setMontant] = useState('');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [adresse, setAdresse] = useState('');
  const [referenceExterne, setReferenceExterne] = useState('');
  const [reference, setReference] = useState(genReference);
  const [responsableId, setResponsableId] = useState('');
  const [observation, setObservation] = useState('');
  const [justifFile, setJustifFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);

  // Sprint 10.1 — Liste des cibles compte/caisse selon le canal sélectionné.
  const targetOptions = useMemo(() => {
    if (canal === 'especes')
      return caisses
        .filter((c) => c.is_active !== false)
        .map((c) => ({ id: c.id, kind: 'caisse' as const, label: c.nom }));
    if (canal === 'ccp' || canal === 'banque')
      return comptes
        .filter((c) => c.is_active !== false)
        .map((c) => ({
          id: c.id,
          kind: 'compte' as const,
          label: c.intitule,
        }));
    return [];
  }, [canal, caisses, comptes]);

  // Reset le compte/caisse quand le canal change.
  useEffect(() => {
    setCaisseOrCompteId('');
  }, [canal]);

  // Auto-sélection si une seule option dispo.
  useEffect(() => {
    if (!caisseOrCompteId && targetOptions.length === 1) {
      setCaisseOrCompteId(targetOptions[0]!.id);
    }
  }, [targetOptions, caisseOrCompteId]);

  // Sélection auto-fill contacts depuis tier
  useEffect(() => {
    if (!tierId) return;
    const t = tiers.find((x) => x.id === tierId);
    if (!t) return;
    setTelephone((cur) => cur || t.telephone || '');
    setEmail((cur) => cur || t.email || '');
    setAdresse((cur) => cur || t.adresse || '');
  }, [tierId, tiers]);

  // Reset on close
  useEffect(() => {
    if (open) return;
    setSens(null);
    setCanal(null);
    setCaisseOrCompteId('');
    setActiviteId('');
    setDateOp(new Date().toISOString().slice(0, 10));
    setHeureOp(new Date().toTimeString().slice(0, 5));
    setTypeCode('');
    setCategorie('autre');
    setMotif('');
    setTierSearch('');
    setTierId('');
    setServiceId('');
    setMontant('');
    setTelephone('');
    setEmail('');
    setAdresse('');
    setReferenceExterne('');
    setReference(genReference());
    setResponsableId('');
    setObservation('');
    setJustifFile(null);
    setError(null);
    setDragOver(false);
    setContactsOpen(false);
  }, [open]);

  // Types par activité (Sprint 6.8.2)
  const activiteCode = activitesScoped.find((a) => a.id === activiteId)?.code ?? null;
  const types = useMemo(() => getOperationTypes(sens, activiteCode), [sens, activiteCode]);

  useEffect(() => {
    if (typeCode && !types.find((t) => t.code === typeCode)) setTypeCode('');
  }, [typeCode, types]);

  useEffect(() => {
    if (!typeCode) return;
    setCategorie(mapTypeCodeToCategorie(typeCode) as OperationCategorie);
  }, [typeCode]);

  // Filtrage tiers : par activité (priorité) + recherche fuzzy nom/tel/email
  const tiersFiltered = useMemo(() => {
    const term = tierSearch.trim().toLowerCase();
    let list = tiers;
    if (activiteId) {
      const same = list.filter(() => true); // activité_principale_id pas dispo, on garde tout
      list = same;
    }
    if (term.length >= 2) {
      list = list.filter(
        (t) =>
          t.raison_sociale.toLowerCase().includes(term) ||
          (t.telephone ?? '').toLowerCase().includes(term) ||
          (t.email ?? '').toLowerCase().includes(term),
      );
    }
    return list.slice(0, 100);
  }, [tiers, tierSearch, activiteId]);

  // Offres / Services par activité
  const offres = useMemo(() => {
    if (!activiteId) return [];
    return formations.filter(() => true).slice(0, 50);
  }, [activiteId, formations]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!sens) return setErrAndToast('Sélectionnez Entrée ou Sortie');
    if (!canal) return setErrAndToast('Sélectionnez le mode de paiement');
    if (!caisseOrCompteId)
      return setErrAndToast(
        canal === 'especes' ? 'Sélectionnez la caisse' : 'Sélectionnez le compte bancaire',
      );
    if (!activiteId) return setErrAndToast('Activité obligatoire');
    if (!eurl?.id) return setErrAndToast('Entreprise non disponible');
    if (!typeCode) return setErrAndToast("Type d'opération obligatoire");
    if (!motif.trim()) return setErrAndToast('Motif obligatoire');
    const m = Number(montant);
    if (!m || m <= 0) return setErrAndToast('Montant > 0 obligatoire');

    function setErrAndToast(msg: string): void {
      setError(msg);
      toast.error(msg);
    }

    try {
      const typeLabel = types.find((t) => t.code === typeCode)?.label;
      const obsParts = [
        typeLabel && `Type : ${typeLabel}`,
        reference && `Réf : ${reference}`,
        referenceExterne && `Réf externe : ${referenceExterne}`,
        responsableId &&
          `Responsable : ${users.find((u) => u.user_id === responsableId)?.full_name ?? responsableId}`,
        observation,
      ].filter(Boolean);

      // Sprint 10.1 — Mapping caisse/compte selon le canal pour respecter
      // operations_canal_target + operations_target_exclusive.
      const isCaisse = canal === 'especes';

      await upsert.mutateAsync({
        entreprise_id: eurl.id,
        date_operation: `${dateOp}T${heureOp}:00`,
        sens,
        canal,
        caisse_id: isCaisse ? caisseOrCompteId : null,
        compte_bancaire_id: isCaisse ? null : caisseOrCompteId,
        categorie,
        activite_id: activiteId,
        activite_beneficiaire_id: activiteId,
        activite_emettrice_id: activiteId,
        tier_id: tierId || null,
        service_id: serviceId || null,
        montant: m,
        montant_dzd: m,
        devise_code: 'DZD',
        taux_change: 1,
        motif: motif.trim(),
        contact_telephone: telephone.trim() || null,
        contact_email: email.trim() || null,
        contact_adresse: adresse.trim() || null,
        bon_recu_ref: referenceExterne.trim() || null,
        observation: obsParts.length > 0 ? obsParts.join(' · ') : null,
        statut: 'validee',
      });

      toast.success(`Opération ${reference} créée`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const msg = (err as Error).message;
      // eslint-disable-next-line no-console
      console.error('[OperationCaisse] submit failed:', err);
      setError(msg);
      toast.error(`Échec création : ${msg}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] max-w-3xl flex-col p-0 sm:max-h-[90dvh]"
        aria-describedby="op-form-desc"
      >
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Nouvelle opération de caisse</DialogTitle>
            <p id="op-form-desc" className="text-xs text-slate-500">
              Saisie d&apos;une entrée ou d&apos;une sortie de caisse — référence{' '}
              <span className="font-mono">{reference}</span>
            </p>
          </DialogHeader>

          <DialogBody className="space-y-5">
            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            )}

            {/* Section 1 — Sens */}
            <div className="grid grid-cols-2 gap-3">
              <SensCard
                label="Entrée"
                sub={SUB_ENTREE}
                icon={<TrendingUp className="h-6 w-6" />}
                color="emerald"
                selected={sens === 'entree'}
                onClick={() => setSens('entree')}
              />
              <SensCard
                label="Sortie"
                sub={SUB_SORTIE}
                icon={<TrendingDown className="h-6 w-6" />}
                color="rose"
                selected={sens === 'sortie'}
                onClick={() => setSens('sortie')}
              />
            </div>

            {/* Section 2 — Mode de paiement + Compte cible */}
            <div className="space-y-2">
              <Label>Mode de paiement</Label>
              <div className="grid grid-cols-3 gap-2">
                <CanalToggle
                  emoji="💵"
                  label="Espèces"
                  selected={canal === 'especes'}
                  onClick={() => setCanal('especes')}
                />
                <CanalToggle
                  emoji="🏤"
                  label="CCP"
                  selected={canal === 'ccp'}
                  onClick={() => setCanal('ccp')}
                />
                <CanalToggle
                  emoji="🏦"
                  label="Banque"
                  selected={canal === 'banque'}
                  onClick={() => setCanal('banque')}
                />
              </div>
              {canal && (
                <Field
                  label={canal === 'especes' ? 'Caisse' : 'Compte bancaire'}
                  warn={targetOptions.length === 0}
                  hint={
                    targetOptions.length === 0
                      ? canal === 'especes'
                        ? '⚠ Aucune caisse définie pour cette entreprise — Paramètres > Banques & Caisses'
                        : '⚠ Aucun compte bancaire défini pour cette entreprise'
                      : `${targetOptions.length} cible${targetOptions.length > 1 ? 's' : ''} disponible${targetOptions.length > 1 ? 's' : ''}`
                  }
                >
                  <select
                    value={caisseOrCompteId}
                    onChange={(e) => setCaisseOrCompteId(e.target.value)}
                    className="input"
                    aria-required="true"
                  >
                    <option value="">— Sélectionner —</option>
                    {targetOptions.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            {/* Section 3 — Activité + Date + Heure */}
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Activité" hint="Caisse cible (ex. Trip Sun, Formation B2C)">
                <select
                  value={activiteId}
                  onChange={(e) => setActiviteId(e.target.value)}
                  className="input"
                  aria-required="true"
                >
                  <option value="">— Sélectionner —</option>
                  {activitesScoped.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nom}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Date">
                <input
                  type="date"
                  value={dateOp}
                  onChange={(e) => setDateOp(e.target.value)}
                  className="input"
                  aria-required="true"
                />
              </Field>
              <Field label="Heure">
                <input
                  type="time"
                  value={heureOp}
                  onChange={(e) => setHeureOp(e.target.value)}
                  className="input"
                  aria-required="true"
                />
              </Field>
            </div>

            {/* Section 4 — Type + Montant */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Type d'opération"
                warn={!sens || !activiteId}
                hint={
                  !sens
                    ? "⚠ Choisissez Entrée ou Sortie d'abord"
                    : !activiteId
                      ? "⚠ Choisissez l'activité d'abord"
                      : `${types.length} type${types.length > 1 ? 's' : ''} disponible${types.length > 1 ? 's' : ''}`
                }
              >
                <select
                  value={typeCode}
                  onChange={(e) => setTypeCode(e.target.value)}
                  className={cn(
                    'input',
                    (!sens || !activiteId) &&
                      'cursor-not-allowed border-amber-300 bg-amber-50 text-amber-900',
                  )}
                  disabled={!sens || !activiteId}
                  aria-required="true"
                  title={
                    !sens
                      ? "Choisissez Entrée ou Sortie d'abord, puis l'activité"
                      : !activiteId
                        ? "Choisissez l'activité d'abord"
                        : undefined
                  }
                >
                  <option value="">— Sélectionner —</option>
                  {types.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.icon} {t.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Montant (DA)">
                <input
                  type="number"
                  inputMode="numeric"
                  value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                  className="input tabular-nums"
                  aria-required="true"
                  placeholder="0"
                />
              </Field>
            </div>

            {/* Motif */}
            <Field label="Motif">
              <input
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                className="input"
                aria-required="true"
                placeholder="Encaissement DP World — Audit Financier 2j"
              />
            </Field>

            {/* Section 5 — Tiers / Client (recherche + select) */}
            <div className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-700">
                Tiers / Client
              </span>
              <div className="grid gap-1 sm:grid-cols-2">
                <label htmlFor="op-tier-search" className="sr-only">
                  Rechercher un tier
                </label>
                <input
                  id="op-tier-search"
                  type="text"
                  value={tierSearch}
                  onChange={(e) => setTierSearch(e.target.value)}
                  placeholder="Rechercher par nom, téléphone, email…"
                  className="input"
                />
                <label htmlFor="op-tier-select" className="sr-only">
                  Sélectionner un tier
                </label>
                <select
                  id="op-tier-select"
                  value={tierId}
                  onChange={(e) => setTierId(e.target.value)}
                  className="input"
                >
                  <option value="">— Sélectionner —</option>
                  {tiersFiltered.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.raison_sociale}
                      {t.telephone ? ` · ${t.telephone}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <span className="mt-0.5 block text-[10px] text-slate-400">
                {tiers.length === 0
                  ? 'Aucun tier disponible pour cette entreprise'
                  : `${tiers.length} tier${tiers.length > 1 ? 's' : ''} dans cette entreprise`}
              </span>
            </div>

            {/* Offre / Service */}
            <Field
              label="Offre / Service"
              warn={!activiteId}
              hint={
                !activiteId
                  ? "⚠ Choisissez l'activité d'abord"
                  : offres.length === 0
                    ? 'Aucune offre — laisser vide'
                    : `${offres.length} offre${offres.length > 1 ? 's' : ''} disponible${offres.length > 1 ? 's' : ''}`
              }
            >
              <select
                value={serviceId}
                onChange={(e) => setServiceId(e.target.value)}
                className={cn(
                  'input',
                  !activiteId && 'cursor-not-allowed border-amber-300 bg-amber-50 text-amber-900',
                )}
                disabled={!activiteId}
                title={!activiteId ? "Choisissez l'activité d'abord" : undefined}
              >
                <option value="">— Sélectionner —</option>
                {offres.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.code} · {f.titre}
                  </option>
                ))}
              </select>
            </Field>

            {/* Section 6 — Référence externe + Responsable (matche Excel REFERENCE_EXTERNE) */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Référence externe"
                hint="N° de versement CCP, n° de reçu, ou label libre (PRIME, BOOST, RETOUR…)"
              >
                <input
                  value={referenceExterne}
                  onChange={(e) => setReferenceExterne(e.target.value)}
                  className="input"
                  placeholder="ex. 312392 ou PRIME"
                />
              </Field>
              <Field label="Responsable" hint="Personne en charge de cette opération">
                <select
                  value={responsableId}
                  onChange={(e) => setResponsableId(e.target.value)}
                  className="input"
                >
                  <option value="">— Sélectionner —</option>
                  {users.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.full_name ?? u.user_id.slice(0, 8)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Coordonnées contact (collapsible — auto-remplies depuis le tier si sélectionné) */}
            <details
              open={contactsOpen}
              onToggle={(e) => setContactsOpen((e.target as HTMLDetailsElement).open)}
              className="rounded-2xl border border-slate-200 bg-slate-50/40"
            >
              <summary className="cursor-pointer px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-100/50">
                Coordonnées de contact (optionnel)
                {tierId && (
                  <span className="ml-2 text-[10px] font-normal italic text-slate-500">
                    Auto-remplies depuis le tier
                  </span>
                )}
              </summary>
              <div className="space-y-3 border-t border-slate-200 px-4 py-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Téléphone">
                    <input
                      type="tel"
                      value={telephone}
                      onChange={(e) => setTelephone(e.target.value)}
                      className="input"
                      placeholder="0555 12 34 56"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input"
                      placeholder="contact@entreprise.dz"
                    />
                  </Field>
                </div>
                <Field label="Adresse">
                  <input
                    value={adresse}
                    onChange={(e) => setAdresse(e.target.value)}
                    className="input"
                    placeholder="Rue, ville, wilaya"
                  />
                </Field>
              </div>
            </details>

            {/* Observation */}
            <Field label="Observation">
              <textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                rows={2}
                className="input min-h-[60px]"
              />
            </Field>

            {/* Justificatif drag-drop */}
            <div>
              <Label>Justificatif (optionnel)</Label>
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const f = e.dataTransfer.files[0];
                  if (f && f.size <= 5 * 1024 * 1024) setJustifFile(f);
                }}
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed px-4 py-6 text-center transition-all',
                  dragOver
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-indigo-300 bg-indigo-50/30 hover:bg-indigo-50',
                )}
              >
                <Upload className="h-6 w-6 text-indigo-500" />
                <span className="text-xs font-medium text-slate-700">
                  {justifFile ? justifFile.name : 'Glisser-déposer PDF/JPG/PNG ou cliquer'}
                </span>
                <span className="text-[10px] text-slate-500">Max 5 Mo</span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && f.size <= 5 * 1024 * 1024) setJustifFile(f);
                    e.target.value = '';
                  }}
                />
              </label>
              {justifFile && (
                <p className="mt-1 text-[10px] text-slate-500">
                  {(justifFile.size / 1024).toFixed(0)} Ko —{' '}
                  <button
                    type="button"
                    onClick={() => setJustifFile(null)}
                    className="text-rose-600 underline"
                  >
                    retirer
                  </button>
                </p>
              )}
              <p className="mt-1 text-[10px] text-slate-400">
                Upload Storage Sprint 6.9 — fichier non transmis pour l&apos;instant.
              </p>
            </div>

            {/* Avance inter-activités info — Sprint 6.7 trigger inactif depuis cette modal */}
            <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-[10px] text-slate-500">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                Cette opération est imputée à l&apos;activité <strong>bénéficiaire</strong> et{' '}
                <strong>émettrice</strong> identique. Pour créer une avance inter-activités,
                utilisez l&apos;onglet « Avances inter-activités ».
              </span>
            </div>

            {/* Aperçu */}
            {sens && montant && (
              <div className="rounded-xl bg-slate-50 p-3 text-xs">
                <span className="font-bold uppercase tracking-widest text-slate-500">Aperçu</span>
                <div className="mt-1 flex items-center justify-between">
                  <span>{sens === 'entree' ? 'Encaissement' : 'Décaissement'}</span>
                  <MoneyAmount
                    amount={sens === 'entree' ? Number(montant) : -Number(montant)}
                    variant="large"
                    withColor
                    showSign
                  />
                </div>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={upsert.isPending}
              className={cn(
                'inline-flex h-10 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold text-white shadow-sm transition-all',
                'bg-indigo-600 hover:bg-indigo-700',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-60',
              )}
            >
              {upsert.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Enregistrer
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SensCard({
  label,
  sub,
  icon,
  color,
  selected,
  onClick,
}: {
  label: string;
  sub: string;
  icon: React.ReactNode;
  color: 'emerald' | 'rose';
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex items-start gap-3 rounded-2xl border-2 p-3 text-left transition-all',
        selected
          ? color === 'emerald'
            ? 'border-emerald-500 bg-emerald-50 shadow-sm'
            : 'border-rose-500 bg-rose-50 shadow-sm'
          : 'border-slate-200 bg-white hover:border-slate-300',
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
          selected
            ? color === 'emerald'
              ? 'bg-emerald-500 text-white'
              : 'bg-rose-500 text-white'
            : 'bg-slate-100 text-slate-500',
        )}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'text-sm font-bold',
            selected
              ? color === 'emerald'
                ? 'text-emerald-700'
                : 'text-rose-700'
              : 'text-slate-700',
          )}
        >
          {label}
        </div>
        <div className="mt-0.5 line-clamp-2 text-[10px] leading-tight text-slate-500">{sub}</div>
      </div>
    </button>
  );
}

function CanalToggle({
  emoji,
  label,
  selected,
  onClick,
}: {
  emoji: string;
  label: string;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex h-12 items-center justify-center gap-2 rounded-xl border-2 px-3 text-sm font-bold transition-all',
        selected
          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
      )}
    >
      <span className="text-base" aria-hidden>
        {emoji}
      </span>
      {label}
    </button>
  );
}

function Label({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-slate-500">
      {children}
    </span>
  );
}

function Field({
  label,
  hint,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  warn?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">{label}</span>
      {children}
      {hint && (
        <span
          className={cn(
            'mt-0.5 block',
            warn ? 'text-xs font-medium text-amber-600' : 'text-[10px] text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
